/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// ForecastChart.jsx — ported from the source app forecasting (verbatim layout).
// Only the recharts axis/grid/legend colors were retuned for Brand Command's
// dark-first theme; the series palette + structure are unchanged.
import React, { useMemo, useState, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Label,
} from "recharts";
import "./ForecastChart.css";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const MONTH_ABBR = MONTHS.map(m => m.slice(0, 3));

// Axis / grid / label colors. recharts writes these as SVG presentation
// attributes (var(--token) wouldn't resolve), so use concrete values that
// read on both the dark (default) and light Brand Command themes.
const AXIS_TEXT = "var(--muted)";  // theme chrome — follows light/dark
const GRID_LINE = "rgba(140,160,190,0.18)";
const AXIS_LINE = "rgba(140,160,190,0.28)";

// Palette — distinguishable hues for many SKUs (lead with brand gold/orange).
const PVA_COLORS = { Forecast: "#F2A33C", Actual: "#4eca8d" };
// …and the line-mode pair, from LINE_COLORS[0] / LINE_COLORS[2] so Plan-vs-Actual
// stays the same two hues as series 1 and 3 (backlog 323).
const PVA_LINE_COLORS = { Forecast: "#C5750D", Actual: "#2E9A65" };

// ⚠️ Must stay byte-identical to CHART_COLORS in `utils/exportForecast.js` —
// these two had the same hues in OPPOSITE order, so the on-screen chart and its
// own export disagreed about which SKU was orange (backlog 166). The retired
// #E85D26 is gone; the app orange #F2A33C leads.
const COLORS = [
  "#F2A33C","#3b82f6","#4eca8d","#a78bfa","#22d3ee",
  "#84cc16","#f472b6","#facc15","#60a5fa","#34d399",
  "#f87171","#c084fc","#fbbf24","#fb923c","#2dd4bf",
];

// LINE MODE ONLY — backlog 323. A 2–2.5px stroke is a graphical object that
// carries meaning, so WCAG 1.4.11 wants 3:1 against its ground. `.chart-area`
// is `var(--surface)` = #FFFFFF light / #1B2A4A dark, and on white COLORS
// measures 1.53:1 (#facc15) to 3.68:1 (#3b82f6) — 14 of 15 fail, the lead
// orange at 2.08:1. Same hues + saturations, lightness squeezed into the band
// that clears 3:1 on BOTH grounds (3.19–4.11:1 on white, 3.46–4.45:1 on navy),
// so one palette serves both themes and the export keeps matching the screen.
// Stacked BARS keep COLORS — a large fill is not a thin line.
// ⚠️ Byte-identical to LINE_COLORS in `utils/exportForecast.js` and
// `depletions-inventory/utils/chartImage.js` (backlog 166 — same reason COLORS
// is).
const LINE_COLORS = [
  "#C5750D","#2C78F5","#2E9A65","#8D69F9","#0D99AF",
  "#629810","#EF3194","#AF8C04","#2282F8","#229C70",
  "#F53636","#AA59FB","#B78503","#D96404","#209B8C",
];

// Custom legend that auto-flows into 2 columns when there are >10 series.
function CompactLegend({ payload, chartType }) {
  if (!payload?.length) return null;
  const useTwoCols = payload.length > 10;
  return (
    <ul style={{
      listStyle: "none",
      margin: 0,
      padding: 0,
      columnCount: useTwoCols ? 2 : 1,
      columnGap: 14,
    }}>
      {payload.map((entry, i) => (
        <li key={`${entry.value}-${i}`} style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 0",
          breakInside: "avoid",
          fontSize: 12,
          color: AXIS_TEXT,
          lineHeight: "20px",
        }}>
          <span style={{
            display: "inline-block",
            width: chartType === "bar" ? 11 : 16,
            height: chartType === "bar" ? 11 : 3,
            background: entry.color,
            borderRadius: chartType === "bar" ? 2 : 0,
            flexShrink: 0,
          }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entry.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

// How the chart can split its series. Each maps a forecast row to one or more
// series labels (tags are multi-valued, so a SKU can land in several).
const GROUP_BY_OPTIONS = [
  { value: "sku",         label: "Product SKU" },
  { value: "region",      label: "Region" },
  { value: "state",       label: "State" },
  { value: "distributor", label: "Distributor" },
  { value: "size",        label: "Bottle Size" },
  { value: "packSize",    label: "Pack Size" },
  { value: "tag",         label: "TAG" },
  { value: "flavor",      label: "Flavor" },
];

export default function ForecastChart({
  selectedState,
  selectedRegion,
  selectedDistributor,
  selectedYears = [],
  selectedSku,
  rawForecast,
  skuNames,
  skuToSize = {},
  skuToPackSize = {},
  skuToFlavor = {},
  skuToTag = {},
  rawActuals = [],
}) {
  const [chartType, setChartType] = useState("line"); // "line" | "bar"
  const [groupBy, setGroupBy] = useState("sku");
  // Plan vs Actual collapses the series to exactly two — the forecast and the
  // depletions that actually happened — so the gap is the whole story.
  const [planVsActual, setPlanVsActual] = useState(false);

  // Resolve the series label(s) a row contributes to for the active grouping.
  const seriesLabelsFor = useCallback((r) => {
    const sku = r.fields.ProductSKU;
    switch (groupBy) {
      case "region":      return [r.fields.Region || "Unspecified"];
      case "state":       return [r.fields.State || "Unspecified"];
      case "distributor": return [r.fields.Distributor || "Unspecified"];
      case "size":        return [skuToSize[sku] || "Unspecified"];
      case "packSize":    return [skuToPackSize[sku] || "Unspecified"];
      case "flavor":      return [skuToFlavor[sku] || "Unspecified"];
      case "tag": {
        const t = skuToTag[sku];
        return Array.isArray(t) && t.length ? t : ["Untagged"];
      }
      case "sku":
      default:            return [sku || "Unspecified"];
    }
  }, [groupBy, skuToSize, skuToPackSize, skuToFlavor, skuToTag]);

  // ─── Build data: [{ month, SeriesA: val, SeriesB: val, ... }, ...] ────────
  // With a single year the x-axis is Jan…Dec (the source app behaviour).
  // With several years selected it becomes a continuous timeline — each year's
  // Jan…Dec laid end to end, labelled "Jan '25", "Feb '25", … so trends read
  // across years left to right. Series are split by the "Group by" dimension.
  const { chartData, seriesKeys } = useMemo(() => {
    const years = [...new Set(selectedYears.map(String))].sort();
    if (years.length === 0) return { chartData: [], seriesKeys: [] };
    const yearSet = new Set(years);
    const multiYear = years.length > 1;

    const filtered = rawForecast.filter(r => {
      if (!yearSet.has(String(r.fields.Year)))                                  return false;
      if (selectedRegion      && r.fields.Region      !== selectedRegion)      return false;
      if (selectedState       && r.fields.State       !== selectedState)       return false;
      if (selectedDistributor && r.fields.Distributor !== selectedDistributor) return false;
      return true;
    });

    const skuFilter = selectedSku ? new Set([selectedSku]) : null;

    // One slot per (year, month) in chronological order.
    const slots = [];
    years.forEach(y => MONTHS.forEach((m, i) => {
      slots.push({
        year: y,
        month: m,
        label: multiYear ? `${MONTH_ABBR[i]} '${y.slice(2)}` : MONTH_ABBR[i],
      });
    }));
    const slotIndex = {}; // `${year}|${month}` -> slot position
    slots.forEach((s, i) => { slotIndex[`${s.year}|${s.month}`] = i; });

    const accumulator = slots.map(() => ({})); // [{ series: total }, …] per slot
    const seriesTotals = {};                   // series -> grand total (for ordering)

    filtered.forEach(r => {
      const sku = r.fields.ProductSKU;
      const month = r.fields.Month;
      const yr = String(r.fields.Year);
      if (!sku || !month) return;
      if (skuFilter && !skuFilter.has(sku)) return;
      const idx = slotIndex[`${yr}|${month}`];
      if (idx == null) return;
      const val = r.fields.CaseForecast ?? 0;
      seriesLabelsFor(r).forEach(series => {
        accumulator[idx][series] = (accumulator[idx][series] ?? 0) + val;
        seriesTotals[series] = (seriesTotals[series] ?? 0) + val;
      });
    });

    // Keep series with any value; biggest first so the legend leads with the
    // most significant lines/bars.
    const activeSeries = Object.keys(seriesTotals)
      .filter(s => seriesTotals[s] > 0)
      .sort((a, b) => seriesTotals[b] - seriesTotals[a] || a.localeCompare(b));

    const chartData = slots.map((s, i) => {
      const row = { month: s.label };
      activeSeries.forEach(series => { row[series] = accumulator[i][series] ?? 0; });
      return row;
    });

    return { chartData, seriesKeys: activeSeries };
  }, [rawForecast, selectedYears, selectedRegion, selectedState, selectedDistributor, selectedSku, seriesLabelsFor]);

  // ─── Plan vs Actual: Forecast and Actual as two series ───────────────────
  const { pvaData, pvaKeys } = useMemo(() => {
    if (!planVsActual) return { pvaData: [], pvaKeys: [] };
    const years = [...new Set(selectedYears.map(String))].sort();
    if (years.length === 0) return { pvaData: [], pvaKeys: [] };
    const yearSet = new Set(years);
    const multiYear = years.length > 1;

    const slots = [];
    years.forEach((y) => MONTHS.forEach((m, i) => {
      slots.push({ year: y, month: m, label: multiYear ? `${MONTH_ABBR[i]} '${y.slice(2)}` : MONTH_ABBR[i] });
    }));
    const slotIndex = {};
    slots.forEach((sl, i) => { slotIndex[`${sl.year}|${sl.month}`] = i; });

    const forecast = slots.map(() => 0);
    const actual = slots.map(() => 0);

    const inScope = (f) => {
      if (!yearSet.has(String(f.Year))) return false;
      if (selectedRegion      && f.Region      !== selectedRegion)      return false;
      if (selectedState       && f.State       !== selectedState)       return false;
      if (selectedDistributor && f.Distributor !== selectedDistributor) return false;
      if (selectedSku         && f.ProductSKU  !== selectedSku)         return false;
      return true;
    };

    for (const r of rawForecast) {
      const f = r.fields;
      if (!inScope(f)) continue;
      const i = slotIndex[`${String(f.Year)}|${f.Month}`];
      if (i != null) forecast[i] += f.CaseForecast ?? 0;
    }
    for (const r of rawActuals) {
      const f = r.fields;
      if (!inScope(f)) continue;
      const i = slotIndex[`${String(f.Year)}|${f.Month}`];
      if (i != null) actual[i] += f.CaseActual ?? 0;
    }

    // Trailing months with no actuals yet are gaps, not zeroes — a flat line to
    // the floor would read as "we sold nothing" instead of "not yet reported".
    let lastActual = -1;
    actual.forEach((v, i) => { if (v > 0) lastActual = i; });

    const pvaData = slots.map((sl, i) => ({
      month: sl.label,
      Forecast: forecast[i],
      Actual: i <= lastActual ? actual[i] : null,
    }));
    return { pvaData, pvaKeys: ["Forecast", "Actual"] };
  }, [planVsActual, rawForecast, rawActuals, selectedYears, selectedRegion, selectedState, selectedDistributor, selectedSku]);

  // ─── Prompt when no year ────────────────────────────────────────────────────
  if (selectedYears.length === 0) {
    return (
      <div className="chart-prompt">
        <div className="chart-prompt-icon">📊</div>
        <div className="chart-prompt-text">Select a <strong>Year</strong> to view the chart.</div>
      </div>
    );
  }

  const activeData = planVsActual ? pvaData : chartData;
  const activeKeys = planVsActual ? pvaKeys : seriesKeys;
  const hasData = activeData.some(row => activeKeys.some(k => (row[k] ?? 0) > 0));

  // ─── Custom tooltip ────────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-label">{label}</div>
        <div className="chart-tooltip-total">
          Total: <strong>{total.toLocaleString()}</strong> cases
        </div>
        <div className="chart-tooltip-divider" />
        {[...payload].reverse().map(p => (
          <div key={p.dataKey} className="chart-tooltip-row">
            <span className="chart-tooltip-swatch" style={{ background: p.color }} />
            <span className="chart-tooltip-name">{p.dataKey}</span>
            <span className="chart-tooltip-value">
              {p.value > 0 ? p.value.toLocaleString() : "—"}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // ─── Y-axis formatter ──────────────────────────────────────────────────────
  const yAxisFormatter = (v) =>
    v >= 1000 ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : String(v);

  // ─── Shared chart props ────────────────────────────────────────────────────
  const sharedProps = {
    data: activeData,
    margin: { top: 8, right: 16, left: 8, bottom: 4 },
  };

  const sharedAxisProps = {
    xAxis: (
      <XAxis
        dataKey="month"
        tick={{ fill: AXIS_TEXT, fontSize: 12 }}
        axisLine={{ stroke: AXIS_LINE }}
        tickLine={false}
      />
    ),
    yAxis: (
      <YAxis
        tickFormatter={yAxisFormatter}
        tick={{ fill: AXIS_TEXT, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={64}
      >
        <Label
          value="Cases"
          angle={-90}
          position="insideLeft"
          offset={12}
          style={{ fill: AXIS_TEXT, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}
        />
      </YAxis>
    ),
    grid: <CartesianGrid strokeDasharray="3 3" stroke={GRID_LINE} vertical={false} />,
    tooltip: (
      <Tooltip
        content={<CustomTooltip />}
        cursor={{ fill: "rgba(232,93,38,0.10)" }}
      />
    ),
    legend: (
      <Legend
        layout="vertical"
        align="right"
        verticalAlign="middle"
        wrapperStyle={{
          fontSize: 12, color: AXIS_TEXT, paddingLeft: 16, lineHeight: "22px",
          maxHeight: "92%", overflowY: "auto", top: "4%",
        }}
        content={<CompactLegend chartType={chartType} />}
      />
    ),
  };

  return (
    <div className="chart-wrap">
      {/* ── Type toggle ── */}
      <div className="chart-controls">
        <div className="chart-type-toggle">
          <button
            className={`chart-type-btn${chartType === "line" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("line")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Line
          </button>
          <button
            className={`chart-type-btn${chartType === "bar" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("bar")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="4" height="18"/><rect x="10" y="8" width="4" height="13"/><rect x="17" y="13" width="4" height="8"/>
            </svg>
            Stacked Bar
          </button>
        </div>

        <div className="chart-group-by">
          <label className="chart-group-label" htmlFor="fc-chart-pva" style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              id="fc-chart-pva"
              type="checkbox"
              checked={planVsActual}
              onChange={e => setPlanVsActual(e.target.checked)}
            />
            Plan vs Actual
          </label>
        </div>

        <div className="chart-group-by">
          <label className="chart-group-label" htmlFor="fc-chart-groupby">Group by</label>
          <select
            id="fc-chart-groupby"
            className="chart-group-select"
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            disabled={planVsActual}
            title={planVsActual ? "Plan vs Actual always compares totals" : undefined}
          >
            {GROUP_BY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {!hasData && (
          <span className="chart-no-data">No data for the selected filters</span>
        )}
      </div>

      {/* ── Chart ── */}
      <div className="chart-area">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart {...sharedProps}>
              {sharedAxisProps.grid}
              {sharedAxisProps.xAxis}
              {sharedAxisProps.yAxis}
              {sharedAxisProps.tooltip}
              {sharedAxisProps.legend}
              {activeKeys.map((sku, i) => {
                // backlog 323 — thin strokes take the 3:1 palette; the dot and
                // the legend swatch (recharts hands `entry.color` = this stroke
                // to CompactLegend) follow it, so the whole mark stays one
                // colour. 2 → 2.5px matches the exported chart's stroke width.
                const lineColor = planVsActual
                  ? PVA_LINE_COLORS[sku]
                  : LINE_COLORS[i % LINE_COLORS.length];
                return (
                  <Line
                    key={sku}
                    type="linear"
                    dataKey={sku}
                    stroke={lineColor}
                    strokeWidth={2.5}
                    strokeDasharray={planVsActual && sku === "Forecast" ? "6 4" : undefined}
                    dot={{ r: 3, fill: lineColor, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          ) : (
            <BarChart {...sharedProps}>
              {sharedAxisProps.grid}
              {sharedAxisProps.xAxis}
              {sharedAxisProps.yAxis}
              {sharedAxisProps.tooltip}
              {sharedAxisProps.legend}
              {activeKeys.map((sku, i) => (
                <Bar
                  key={sku}
                  dataKey={sku}
                  stackId={planVsActual ? undefined : "stack"}
                  fill={planVsActual ? PVA_COLORS[sku] : COLORS[i % COLORS.length]}
                  radius={i === activeKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
